import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import ScrollAnimation from "../components/ScrollAnimation";
import { HERO } from "../constants/copy";

/* ================== ENV ================== */
const GITHUB_USERNAME = process.env.REACT_APP_GITHUB_USERNAME;
const REPO_NAME = process.env.REACT_APP_GITHUB_REPO;
const BRANCH = process.env.REACT_APP_GITHUB_BRANCH;
const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN;

/* ================== AXIOS ================== */
const githubApi = axios.create({
  baseURL: "https://api.github.com",
  headers: {
    Authorization: `Bearer ${GITHUB_TOKEN}`,
    Accept: "application/vnd.github+json",
  },
});

/* ================== SKELETON ================== */
const BlogSkeleton = () => (
  <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 shadow-card animate-pulse">
    <div className="aspect-[4/3] bg-white/10" />
    <div className="p-6 space-y-3">
      <div className="h-3 w-24 bg-white/10 rounded" />
      <div className="h-5 w-3/4 bg-white/10 rounded" />
      <div className="h-4 w-full bg-white/10 rounded" />
      <div className="h-4 w-5/6 bg-white/10 rounded" />
    </div>
  </div>
);

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBlog, setActiveBlog] = useState(null);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const cached = sessionStorage.getItem("blogs");
        if (cached) {
          setBlogs(JSON.parse(cached));
          setLoading(false);
          return;
        }

        const folderRes = await githubApi.get(
          `/repos/${GITHUB_USERNAME}/${REPO_NAME}/contents/blogs`
        );

        const files = folderRes.data.filter((f) => f.type === "file");

        const posts = await Promise.all(
          files.map(async (file) => {
            try {
              const res = await githubApi.get(file.url);
              return JSON.parse(atob(res.data.content));
            } catch {
              return null;
            }
          })
        );

        const validPosts = posts
          .filter(Boolean)
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        setBlogs(validPosts);
        sessionStorage.setItem("blogs", JSON.stringify(validPosts));
      } catch (err) {
        console.error("Failed to load blogs", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  /* Close popup on ESC */
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && setActiveBlog(null);
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  const card3DVariants = {
    rest: { rotateX: 0, rotateY: 0 },
    hover: { rotateX: -5, rotateY: 5, transition: { duration: 0.3 } },
  };

  return (
    <>
      <section className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 py-10 sm:py-14 md:py-20 lg:py-24">
        <div className="max-w-6xl mx-auto">

          {/* Heading */}
          
            <h1 className="section-heading mb-3 sm:mb-4">Blogs</h1>
            <p className="section-sub mb-8 sm:mb-12">
              Stories, tips, and behind-the-scenes from weddings we've captured.{" "}
              {HERO.tagline}
            </p>
         

          {/* Blog Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-10 lg:gap-12">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <BlogSkeleton key={i} />
                ))
              : blogs.map((blog, index) => (
                 
                    <div
                      className="card-3d cursor-pointer"
                      variants={card3DVariants}
                      initial="rest"
                      whileHover="hover"
                      style={{ perspective: 1000 }}
                      onClick={() => setActiveBlog(blog)}
                    >
                      <div className="group rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-weddmac-gold/30 shadow-card card-hover">
                        <div className="aspect-[4/3] overflow-hidden">
                          <img
                            src={`https://raw.githubusercontent.com/${GITHUB_USERNAME}/${REPO_NAME}/${BRANCH}/${blog.image}`}
                            alt={blog.title}
                            loading="eager"
                            className="w-full h-full object-cover img-zoom"
                          />
                        </div>

                        <div className="p-5 sm:p-6 md:p-7">
                          <time className="text-weddmac-gold/80 text-xs tracking-wide">
                            {blog.date}
                          </time>
                          <h2 className="font-inria font-light text-lg sm:text-xl md:text-2xl text-white mt-2 mb-3">
                            {blog.title}
                          </h2>
                          <p className="text-white/75 text-sm sm:text-base leading-relaxed">
                            {blog.excerpt}
                          </p>
                        </div>
                      </div>
                    </div>
                 
                ))}
          </div>
        </div>
      </section>

      {/* ================= BLOG POPUP ================= */}
      
        {activeBlog && (
          <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveBlog(null)}
          >
            <div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="relative max-w-3xl w-full bg-[#0b1a2a] rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close */}
              <button
                onClick={() => setActiveBlog(null)}
                className="absolute top-4 right-4 text-white/70 hover:text-white text-xl"
              >
                ✕
              </button>

              {/* Image */}
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={`https://raw.githubusercontent.com/${GITHUB_USERNAME}/${REPO_NAME}/${BRANCH}/${activeBlog.image}`}
                  alt={activeBlog.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="p-6 md:p-8">
                <time className="text-weddmac-gold/80 text-xs tracking-wide">
                  {activeBlog.date}
                </time>
                <h2 className="font-inria font-light text-2xl md:text-3xl text-white mt-2 mb-4">
                  {activeBlog.title}
                </h2>
                <p className="text-white/80 leading-relaxed">
                  {activeBlog.excerpt}
                </p>
              </div>
            </div>
          </div>
        )}
      
    </>
  );
}
